<template>
  <div class="w-full flex items-center justify-between px-1">
    <div class="flex relative pl-3 w-[100px]">
      <a
        v-if="!isOnFirstPage"
        :href="generatePaginationHref(pagination.page - 1)"
        class="inline-flex items-center text-sm font-bold text-los-blue-dark hover:underline focus-within:underline group"
        title="Vorige pagina"
      >
        <FontAwesome
          class="mr-1 absolute left-0 group-focus:-left-1 group-hover:-left-1 transition-all"
          icon="chevron-left"
        />
        Vorige
      </a>
    </div>

    <div
      v-if="hasMultiplePages"
      class="hidden md:flex items-end py-1 rounded-md text-los-blue-dark"
    >
      <a
        :href="generatePaginationHref(1)"
        class="inline-flex items-center border-t-2 border-dashed border-transparent px-4 text-sm hover:underline"
        :class="{
          'font-bold text-los-blue-darkest': pagination.page === 1,
        }"
        :title="getButtonTitle(1)"
        >1</a
      >
      <span v-if="isFurtherThanPageThree" class="text-los-blue-base">...</span>
      <template v-for="index in reverseArray([1])">
        <a
          v-if="pagination.page - index > 1"
          :key="index + 'pre'"
          :href="generatePaginationHref(pagination.page - 1)"
          class="inline-flex items-center border-t-2 border-dashed border-transparent px-4 text-sm text-los-blue-dark hover:underline"
          :title="getButtonTitle(pagination.page - 1)"
        >
          {{ pagination.page - 1 }}
        </a>
      </template>
      <a
        v-if="pagination.page !== 1 && pagination.page < pagination.pageCount"
        :href="generatePaginationHref(pagination.page)"
        :title="getButtonTitle(pagination.page)"
        class="inline-flex items-center px-4 text-sm font-bold text-los-blue-darkest"
        aria-current="page"
      >
        {{ pagination.page }}
      </a>
      <a
        v-if="pagination.page + 1 < pagination.pageCount"
        :href="generatePaginationHref(pagination.page + 1)"
        :title="getButtonTitle(pagination.page + 1)"
        class="inline-flex items-center border-t-2 border-dashed border-transparent px-4 text-sm text-los-blue-dark hover:underline"
      >
        {{ pagination.page + 1 }}
      </a>
      <span
        v-if="pagination.page + 2 < pagination.pageCount"
        class="text-los-blue-base"
        >...</span
      ><a
        :href="generatePaginationHref(pagination.pageCount)"
        :title="getButtonTitle(pagination.pageCount)"
        class="inline-flex items-center border-t-2 border-dashed border-transparent px-4 text-sm hover:underline"
        :class="{
          'font-bold text-los-blue-darkest':
            pagination.page === pagination.pageCount,
        }"
      >
        {{ pagination.pageCount }}
      </a>
    </div>

    <div class="flex justify-end relative pr-3 w-[100px]">
      <a
        v-if="isNotOnLastPage"
        :href="generatePaginationHref(pagination.page + 1)"
        class="inline-flex items-center text-sm font-bold text-los-blue-dark hover:underline focus-within:underline group"
        title="Volgende pagina"
      >
        Volgende
        <FontAwesome
          class="absolute right-0 group-focus:-right-1 group-hover:-right-1 transition-all"
          icon="chevron-right"
        />
      </a>
    </div>
  </div>
</template>

<script setup>
const route = useRoute();

const props = defineProps({
  pagination: {
    type: Object,
    required: true,
  },
});

const hasMultiplePages = computed(() => {
  return props.pagination.pageCount > 1;
});
const isOnFirstPage = computed(() => {
  return props.pagination.page === 1;
});
const isNotOnLastPage = computed(() => {
  return props.pagination.pageCount > props.pagination.page;
});
const isFurtherThanPageThree = computed(() => {
  return props.pagination.page > 3;
});

function reverseArray(value) {
  return value.slice().reverse();
}

function getButtonTitle(index) {
  const firstItemNr = props.pagination.pageSize * (index - 1) + 1;
  const possibleLastItemNr = props.pagination.pageSize * index;
  const lastItemNr =
    props.pagination.total < possibleLastItemNr
      ? props.pagination.total
      : possibleLastItemNr;

  return `Resultaten ${firstItemNr} t/m ${lastItemNr}`;
}

function generatePaginationHref(pageNumber) {
  const searchParams = new URLSearchParams({
    ...route.query,
    page: pageNumber,
  });
  return `${route.path}?${searchParams.toString()}`;
}
</script>

<style lang="scss" scoped></style>
